/**
 * Hook para gerenciar composição de emails
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendEmail, uploadEmailAttachment } from '../services/emailService';
import type { ComposeEmailData, ComposeState, UploadedAttachment } from '../types/email.types';

const EMPTY_COMPOSE: ComposeEmailData = {
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  body: '',
  attachments: [],
};

export const useEmailCompose = () => {
  const queryClient = useQueryClient();
  const [composeState, setComposeState] = useState<ComposeState>('closed');
  const [composeData, setComposeData] = useState<ComposeEmailData>(EMPTY_COMPOSE);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const sendMutation = useMutation({
    mutationFn: (data: ComposeEmailData) => sendEmail(data),
    onSuccess: (_result, data) => {
      queryClient.invalidateQueries({ queryKey: ['email-conversations'] });
      // Responder dentro de uma thread aberta: a lista de mensagens do painel de
      // leitura precisa recarregar, senão a resposta enviada só aparece ao
      // remontar o componente. Era o `quickReplyMutation` que fazia isso antes da
      // caixa inline sair (2026-08-25) — o popup passou a ser o único caminho.
      if (data.replyToConversationId) {
        queryClient.invalidateQueries({ queryKey: ['email-messages', data.replyToConversationId] });
      }
      closeCompose();
    },
  });

  const openCompose = useCallback((replyData?: Partial<ComposeEmailData>) => {
    setComposeData({
      ...EMPTY_COMPOSE,
      ...replyData,
    });
    setComposeState('normal');
    setShowCc(!!replyData?.cc?.length);
    setShowBcc(!!replyData?.bcc?.length);
  }, []);

  const handleFileUpload = useCallback(async (files: FileList) => {
    setIsUploading(true);
    try {
      const uploads = await Promise.all(
        Array.from(files).map(async (file) => {
          const result = await uploadEmailAttachment(file);
          return {
            url: result.url,
            filename: file.name,
            size: file.size,
            mimeType: file.type,
          } as UploadedAttachment;
        })
      );
      setUploadedAttachments((prev) => [...prev, ...uploads]);
    } catch {
      // Upload failed silently
    } finally {
      setIsUploading(false);
    }
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setUploadedAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const closeCompose = useCallback(() => {
    setComposeState('closed');
    setComposeData(EMPTY_COMPOSE);
    setShowCc(false);
    setShowBcc(false);
    setUploadedAttachments([]);
  }, []);

  const minimizeCompose = useCallback(() => {
    setComposeState('minimized');
  }, []);

  const maximizeCompose = useCallback(() => {
    setComposeState(composeState === 'fullscreen' ? 'normal' : 'fullscreen');
  }, [composeState]);

  const restoreCompose = useCallback(() => {
    setComposeState('normal');
  }, []);

  const updateField = useCallback(<K extends keyof ComposeEmailData>(
    field: K,
    value: ComposeEmailData[K]
  ) => {
    setComposeData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const send = useCallback(() => {
    if (!composeData.to.length || !composeData.subject || !composeData.channelId) return;
    sendMutation.mutate(composeData);
  }, [composeData, sendMutation]);

  const hasContent = composeData.body.length > 0 || composeData.subject.length > 0 || composeData.to.length > 0 || uploadedAttachments.length > 0;

  return {
    composeState,
    composeData,
    showCc,
    showBcc,
    setShowCc,
    setShowBcc,
    openCompose,
    closeCompose,
    minimizeCompose,
    maximizeCompose,
    restoreCompose,
    updateField,
    send,
    isSending: sendMutation.isPending,
    uploadedAttachments,
    isUploading,
    hasContent,
    handleFileUpload,
    removeAttachment,
  };
};
